
import { IonPage } from '@ionic/react';
import { useState, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_CONFIG from '../config/api';
import AuthNavigation from '../components/AuthNavigation';
import '../styles/auth.css';

const Login = () => {
  const history = useHistory();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Auto-redirect if already logged in
  useEffect(() => {
    const userId = sessionStorage.getItem('user_id');
    const roleId = sessionStorage.getItem('role_id') || localStorage.getItem('roleId');

    if (userId) {
      const queryParams = new URLSearchParams(location.search);
      const redirectPath = queryParams.get('redirect');

      if (redirectPath) {
        history.replace(redirectPath);
      } else {
        // Redirect to dashboard
        if (roleId) {
          const sRoleId = String(roleId);
          console.log('Login Initial Check - Role found:', sRoleId);
          if (sRoleId === '1') {
            history.replace('/super-admin');
          } else if (sRoleId === '2') {
            history.replace('/org-admin');
          } else if (sRoleId === '4') {
            history.replace('/page/view?m=t');
          } else {
            // Default for other roles
            history.replace('/page/view?m=t');
          }
        } else {
          // Fallback if roleId is not found
          history.replace('/page/view?m=t');
        }
      }
    }
  }, [history, location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post(API_CONFIG.LOGIN, { email, password }, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.data.success) {
        sessionStorage.setItem('user_id', response.data.user_id);

        // Store user role
        if (response.data.role_id) {
          sessionStorage.setItem('role_id', response.data.role_id);
          localStorage.setItem('roleId', response.data.role_id);
        }

        // Store organization info if user belongs to an organization
        if (response.data.organization_id) {
          sessionStorage.setItem('organization_id', response.data.organization_id);
          if (response.data.organization_name) {
            sessionStorage.setItem('organization_name', response.data.organization_name);
          }
          localStorage.setItem('organizationId', response.data.organization_id);
        } else {
          // Clear organization info if user doesn't belong to one
          sessionStorage.removeItem('organization_id');
          sessionStorage.removeItem('organization_name');
          localStorage.removeItem('organizationId');
        }


        // Store userId and login timestamp in localStorage for persistence
        const now = new Date().getTime();
        localStorage.setItem('userId', response.data.user_id);
        localStorage.setItem('login_timestamp', now.toString());

        // Redirect based on Role
        const roleId = response.data.role_id;

        const queryParams = new URLSearchParams(location.search);
        const redirectPath = queryParams.get('redirect');

        if (redirectPath) {
          history.push(redirectPath);
          return; // Stop further redirects
        } else if (roleId === '1' || roleId === 1) {
          history.push('/super-admin');
        } else if (roleId === '4' || roleId === 4) {
          history.push('/user-dashboard');
        } else if (roleId === '2' || roleId === 2) {
          history.push('/org-admin');
        } else {
          history.push('/page/view?m=t');
        }
      } else {
        setError('Invalid email or password');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Login failed: Invalid email or password';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <IonPage>
      <div className="auth-container">
        {/* Floating background elements */}
        <div className="floating-element"></div>
        <div className="floating-element-2"></div>

        {/* Custom Header integrated into background */}
        <div className="auth-header">
          <button onClick={() => history.goBack()} className="back-button">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Back</span>
          </button>
          <h2 className="auth-title-header">Sign In</h2>
          <div className="w-16"></div> {/* Spacer for centering */}
        </div>

        <div className="auth-content">
          <div className="auth-card animate-fade-in-up">
            {/* Logo */}
            <div className="text-center mb-8">
              <img
                className="auth-logo animate-bounce-in"
                src="/assets/final_peoppoll_logo.png"
                alt="PeoPoll Logo"
              />
              <h1 className="auth-title">Welcome Back</h1>
              <p className="auth-subtitle">Sign in to your account to continue</p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="error-message animate-fade-in-up">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {error}
                </div>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="form-group">
                <label className="form-label">
                  <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="form-input"
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Password
                </label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="form-input pr-12"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.543 7-1.275 4.057-5.065 7-9.543 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className={`btn-primary ${isLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing In...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                    </svg>
                    Sign In
                  </div>
                )}
              </button>

              {/* Forgot Password */}
              <div className="text-center">
                <Link to="/page/reset-password" className="link-text text-sm">
                  Forgot your password?
                </Link>
              </div>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Don't have an account?</span>
                </div>
              </div>

              {/* Register Link */}
              <Link to="/page/register" className="btn-secondary text-center block">
                <div className="flex items-center justify-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  Create New Account
                </div>
              </Link>
            </form>

            {/* Cross-navigation to other auth pages */}
            <AuthNavigation />
          </div>
        </div>

      </div>
    </IonPage>
  );
};

export default Login;
